import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, ToastViewport } from './components/foundational/Toast';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { aplicarViewportNativo } from './lib/viewportNativo';

// Escape del app shell para la demo del congreso (QR del póster Bupa): si un
// service worker de una versión anterior sirvió la SPA en /demo (la denylist
// nueva ya deja pasar esa ruta a la red, pero el SW del navegador se
// actualiza recién al abrir y puede tardar una visita entera), la propia app
// reenvía a la página estática apenas corre su bundle. Sin esto, quien
// escaneaba el QR con MiPAE ya visitada veía el embudo de registro en vez de
// la demo (le pasó al fundador la noche antes del congreso). En nativo el
// pathname jamás es /demo, así que el reenvío no puede disparar ahí.
if (/^\/demo(?:-congreso)?\/?$/.test(window.location.pathname)) {
  const utm =
    window.location.search ||
    '?utm_source=bupa&utm_medium=qr-demo&utm_campaign=congreso-2026-08';
  window.location.replace('/demo-congreso/index.html' + utm);
}

if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    release: process.env.REACT_APP_VERSION,
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network request failed$/,
      /^cancelled$/i,
      // Ruido del puente JS de WebView de Android (navegadores in-app):
      // el objeto nativo se recolecta al destruir la WebView. No accionable.
      /Error invoking postMessage/,
      // Scripts que navegadores in-app / extensiones inyectan para leer el
      // JSON-LD de la página. No es código nuestro y no afecta a la app.
      /\["@context"\]/,
      // supabase-js pide un lock del navegador para renovar la sesión; si otra
      // pestaña o una renovación lo "roba" (opción steal), la librería ya lo
      // recupera sola y reintenta. Llega como rechazo no manejado, sin efecto
      // para la persona (MIPAE-J, MIPAE-Q, 30-sep-2026).
      /Lock "lock:sb-.*" was released because another request stole it/,
      /Lock broken by another request with the 'steal' option/,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    beforeSend(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers.authorization;
          delete event.request.headers.cookie;
        }
      }
      const SENSITIVE = /valoracion|bitacora|henderson|entry|input_clinico/i;
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.filter(
          b => !(b.message && SENSITIVE.test(b.message)) &&
               !(b.data && SENSITIVE.test(JSON.stringify(b.data)))
        );
      }
      return event;
    },
  });
  // Exponer en window para que ErrorBoundary pueda llamar captureException
  // sin importar @sentry/react desde el class component directamente.
  window.Sentry = Sentry;

  // session_id ya existe en sessionStorage desde el login.
  // Lo leemos acá para taggear todos los eventos sin tocar el flow de auth.
  try {
    const sessionId = sessionStorage.getItem('mipae:session_id');
    if (sessionId) {
      Sentry.setTag('session_id', sessionId);
    }
  } catch (e) {
    // sessionStorage puede fallar en modo incógnito o iframes — silencioso
  }
}

// Antes de montar: en la app instalada el viewport queda sin zoom (ver
// lib/viewportNativo.js). En web no se toca, el pellizco es accesibilidad.
aplicarViewportNativo({ nativo: Capacitor.isNativePlatform() });

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <App />
          <ToastViewport />
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// En la app nativa (Capacitor) los assets ya van empaquetados dentro del APK y
// cargan sin conexión por sí solos. Un service worker (feature de PWA web) ahí
// solo estorba: su fallback a offline.html secuestra el arranque sin internet,
// dejando la app pegada en "Sin conexión" antes de que React siquiera corra.
// Por eso lo desregistramos y limpiamos sus cachés en nativo; en web (browser)
// se mantiene el comportamiento PWA de siempre.
if (Capacitor.isNativePlatform()) {
  serviceWorkerRegistration.unregister();
  if (typeof caches !== 'undefined' && caches.keys) {
    caches.keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {});
  }
} else {
  serviceWorkerRegistration.register();
}
